import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Button, Tag, Empty, Alert, Popconfirm, Spin, Input } from 'antd';
import { LinkOutlined, DisconnectOutlined } from '@ant-design/icons';
import SinistreSearch from '../../../components/SinistreSearch';
import TableMembresLiaison from './TableMembresLiaison';
import { liaisonService } from '../../../services/liaisonService';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const GREEN = '#1a6b3c';

/**
 * Onglet « Lier des dossiers sinistres » de la fiche d'un dossier.
 *
 * POURQUOI UN ONGLET ET NON UNE MODALE. Le geste vivait derrière un bouton de la
 * barre d'actions, qui ouvrait une fenêtre. Deux conséquences : on ne voyait pas,
 * en consultant un dossier, avec quels autres il était groupé - seul un tag portait
 * le numéro de liaison, et il fallait quitter la fiche pour la page dédiée ; et le
 * formulaire n'apparaissait qu'après avoir su qu'un bouton l'ouvrait. L'onglet
 * montre les deux d'emblée : ce à quoi ce dossier est déjà lié, et de quoi l'y
 * rattacher.
 *
 * CE QUE LE SERVEUR REFUSE, ET CE QUE CET ÉCRAN PRÉVIENT. Le backend refuse de lier
 * un dossier à lui-même, de mélanger deux `typeSinistre` (ET/TE), et de lier deux
 * dossiers appartenant chacun déjà à une liaison différente. La recherche évite les
 * deux premiers - elle est filtrée sur le type du dossier courant et en exclut le
 * dossier lui-même. Le troisième remonte tel quel : le message du serveur est écrit
 * pour être lu.
 *
 * PLUSIEURS DOSSIERS À LA FOIS. Une liaison n'est pas limitée à deux dossiers : un
 * carambolage en compte autant qu'il y a de véhicules, et ils se déclarent rarement
 * le même jour. On accumule donc une liste avant de valider.
 */
export default function PanneauLiaison({ sinistre, peutLier, onChangement }) {
  const { t } = useTranslation('dossierSinistre');
  const [cibles, setCibles] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const [membres, setMembres] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [detachement, setDetachement] = useState(false);

  /* Recit de l'accident tel qu'on le comprend une fois les dossiers reunis.
     Facultatif : il se precise souvent apres le rapprochement, et l'exiger ici
     aurait surtout produit des « RAS ». Laisse vide, il n'efface pas celui que
     le groupe porte deja. */
  const [nouvellesCirconstances, setNouvellesCirconstances] = useState('');

  const liaisonId = sinistre?.liaisonTrackingId;

  /* Les membres de la liaison, en UN appel. `lot` est prévu pour cela ; la page des
     liaisons et la liste des sinistres l'utilisent déjà de la même façon. */
  const chargerMembres = useCallback(async () => {
    if (!liaisonId) { setMembres([]); return; }
    setChargement(true);
    try {
      const r = await liaisonService.lot([liaisonId]);
      const lot = (r.data?.data || []).find((l) => l.liaisonTrackingId === liaisonId);
      setMembres(lot?.membres || []);
    } catch {
      setMembres([]);
    } finally {
      setChargement(false);
    }
  }, [liaisonId]);

  useEffect(() => { chargerMembres(); }, [chargerMembres]);

  // Identité stable : sans cela SinistreSearch redéfinirait sa recherche à chaque rendu.
  const extraParams = useMemo(
    () => (sinistre?.typeSinistre ? { typeSinistre: sinistre.typeSinistre } : undefined),
    [sinistre?.typeSinistre],
  );

  /* Le dossier courant, ceux déjà retenus, et ceux déjà dans la liaison : les
     reproposer produirait un refus du serveur que l'écran pouvait éviter. */
  const excludeTrackingIds = useMemo(
    () => [
      sinistre?.sinistreTrackingId,
      ...cibles.map((c) => c.trackingId),
      ...membres.map((m) => m.sinistreTrackingId),
    ].filter(Boolean),
    [sinistre?.sinistreTrackingId, cibles, membres],
  );

  const ajouter = (trackingId, objet) => {
    if (!trackingId || cibles.some((c) => c.trackingId === trackingId)) return;
    setCibles((liste) => [...liste, {
      trackingId,
      numero: objet?.numeroSinistreLocal || objet?.numeroSinistreHomologue || trackingId,
    }]);
  };

  const retirer = (trackingId) =>
    setCibles((liste) => liste.filter((c) => c.trackingId !== trackingId));

  /**
   * Un appel par dossier retenu, et les refus sont rapportés dossier par dossier.
   *
   * Le serveur oppose ses règles à chaque rattachement séparément : si trois
   * dossiers sur quatre passent, l'utilisateur doit savoir lequel a échoué et
   * pourquoi - pas voir l'ensemble refusé sans explication. Les dossiers liés le
   * restent ; seuls les refusés demeurent dans la liste.
   */
  const confirmer = async () => {
    if (cibles.length === 0) { toast.error(t('panneauLiaison.selectionnezUn')); return; }
    setEnvoi(true);

    const echecs = [];
    let reussites = 0;

    for (const cible of cibles) {
      try {
        await liaisonService.lier(
          sinistre.sinistreTrackingId, cible.trackingId, nouvellesCirconstances.trim());
        reussites += 1;
      } catch (e) {
        echecs.push({ ...cible, motif: e?.response?.data?.message || t('panneauLiaison.liaisonRefusee') });
      }
    }

    setEnvoi(false);
    if (reussites > 0) {
      toast.success(reussites === 1 ? t('panneauLiaison.dossierLie') : t('panneauLiaison.dossiersLies', { n: reussites }));
      await onChangement?.();
    }
    echecs.forEach((e) => toast.error(`${e.numero} : ${e.motif}`));
    setCibles(echecs.map(({ trackingId, numero }) => ({ trackingId, numero })));
  };

  const detacher = async () => {
    setDetachement(true);
    try {
      await liaisonService.detacher(sinistre.sinistreTrackingId);
      toast.success(t('panneauLiaison.detache'));
      await onChangement?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('panneauLiaison.detachementRefuse'));
    } finally {
      setDetachement(false);
    }
  };

  if (!peutLier) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="warning" showIcon
          message={t('panneauLiaison.reserve')}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Alert
        type="info" showIcon
        style={{ marginBottom: 16, borderRadius: 10 }}
        message={liaisonId
          ? t('panneauLiaison.appartient', { numero: sinistre.numeroLiaison })
          : t('panneauLiaison.aucuneLiaison')}
        description={t('panneauLiaison.explication')}
      />

      <div className="cb-card" style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>
          {liaisonId ? t('panneauLiaison.ajouter') : t('panneauLiaison.lierAUnAutre')}
        </div>
        <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 12 }}>
          {t('panneauLiaison.aideRecherche')}
        </div>

        <SinistreSearch
          value={undefined}
          onSelect={ajouter}
          placeholder={t('panneauLiaison.placeholderRecherche')}
          extraParams={extraParams}
          excludeTrackingIds={excludeTrackingIds}
        />

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 6 }}>
            {t('panneauLiaison.dossiersALier')}
          </div>
          {cibles.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span style={{ fontSize: 12.5 }}>{t('panneauLiaison.aucunSelectionne')}</span>}
              style={{ margin: '8px 0' }}
            />
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {cibles.map((c) => (
                <Tag
                  key={c.trackingId}
                  icon={<LinkOutlined />}
                  closable
                  onClose={(e) => { e.preventDefault(); retirer(c.trackingId); }}
                  color="geekblue"
                  style={{ borderRadius: 14, fontSize: 12, padding: '2px 8px' }}
                >
                  {c.numero}
                </Tag>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginBottom: 6 }}>
            {t('panneauLiaison.nouvellesCirconstances')} <span style={{ color: '#9ca3af' }}>{t('panneauLiaison.facultatif')}</span>
          </div>
          <Input.TextArea
            rows={3}
            maxLength={4000}
            showCount
            value={nouvellesCirconstances}
            onChange={(e) => setNouvellesCirconstances(e.target.value)}
            placeholder={t('panneauLiaison.circonstancesPlaceholder')}
          />
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4 }}>
            {t('panneauLiaison.circonstancesAide')}
          </div>
        </div>

        <Button
          type="primary"
          icon={<LinkOutlined />}
          loading={envoi}
          disabled={cibles.length === 0}
          onClick={confirmer}
          style={{ marginTop: 14, borderRadius: 8, background: GREEN, borderColor: GREEN }}
        >
          {cibles.length > 1 ? t('panneauLiaison.lierN', { n: cibles.length }) : t('panneauLiaison.lier')}
        </Button>
      </div>

      {liaisonId && (
        <div className="cb-card" style={{ padding: 20 }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexWrap: 'wrap', marginBottom: 12,
          }}>
            <div>
              <div style={{ fontWeight: 600, color: '#111827' }}>
                {t('panneauLiaison.dossiersDeLaLiaison', { numero: sinistre.numeroLiaison })}
              </div>
              <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                {t('panneauLiaison.moinsAvance')}
              </div>
            </div>
            <Popconfirm
              title={t('panneauLiaison.detacherTitre')}
              description={t('panneauLiaison.detacherDetail')}
              onConfirm={detacher}
              okText={t('panneauLiaison.detacher')} cancelText={t('panneauLiaison.annuler')}
              okButtonProps={{ danger: true, loading: detachement }}
            >
              <Button danger icon={<DisconnectOutlined />} style={{ borderRadius: 8 }}>
                {t('panneauLiaison.detacherCeDossier')}
              </Button>
            </Popconfirm>
          </div>

          {chargement
            ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
            : <TableMembresLiaison membres={membres} />}
        </div>
      )}
    </div>
  );
}
